import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import {
  PRIMARY_TABS,
  primaryTabForPath,
  watchHref,
  todayHref,
  type PrimaryTab,
} from "../../lib/primaryNav";
import { getActiveChartId } from "../../lib/chartSession";

/**
 * PrimaryNav — the 5-tab shell (Today · Watch · Ask · Listen · Discover).
 * `variant="top"` for desktop header strip; `variant="bottom"` for mobile bar.
 */
export default function PrimaryNav({
  chartId,
  variant = "top",
}: {
  chartId?: string | null;
  variant?: "top" | "bottom";
}) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const active = primaryTabForPath(pathname);
  const recent = chartId ?? getActiveChartId();

  function hrefFor(tab: PrimaryTab): string {
    switch (tab) {
      case "today":
        return todayHref(recent);
      case "watch":
        return watchHref(recent);
      case "ask":
        return "/ask";
      case "listen":
        return "/listen";
      case "discover":
        return "/discover";
      default:
        return "/";
    }
  }

  // One accessible landmark at a time: top is desktop-only CSS; bottom is
  // mobile-only CSS. Hide the inactive variant from AT for a11y.
  const landmarkLabel = variant === "bottom"
    ? t("ui.web.primary_nav.mobile_landmark")
    : t("ui.web.primary_nav.primary_landmark");

  return (
    <nav
      className={`primary-nav primary-nav--${variant}`}
      aria-label={landmarkLabel}
    >
      <ol className="primary-nav__track">
        {PRIMARY_TABS.map((t) => {
          const isActive = t.id === active;
          const tabLabel = translateTab(t.messageKey);
          return (
            <li key={t.id} className={isActive ? "is-active" : undefined}>
              <Link
                to={hrefFor(t.id)}
                className="primary-nav__link"
                aria-current={isActive ? "page" : undefined}
                // Task 2 §8: "Watch" must be understandable as the
                // chart-reading/casting journey. The visual label is
                // preserved (deeply established across iOS/Android/web);
                // a visually-hidden clarifier is added so screen-reader
                // users hear "Watch, chart reading and casting".
                aria-label={
                  t.id === "watch"
                    ? translateTab("ui.web.primary_nav.watch_clarifier")
                    : undefined
                }
              >
                <span className="primary-nav__glyph" aria-hidden="true">
                  {t.glyph}
                </span>
                <span className="primary-nav__label">{tabLabel}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );

  function translateTab(key: string): string {
    return t(key);
  }
}
